import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Settings, FileText, Shield, ShoppingCart, ScanBarcode, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function AdminSettings() {
  const { getAuthHeader } = useAuth();
  const [appSettings, setAppSettings] = useState({
    receiving_validation_mode: "override",
    card_template_size: "3x5"
  });
  const [affiliateSettings, setAffiliateSettings] = useState({
    amazon_affiliate_tag: ""
  });
  const [stationSettings, setStationSettings] = useState({
    station_mode_enabled: false,
    scan_autosubmit_enabled: true,
    scan_idle_submit_ms: 200,
    scan_submit_cooldown_ms: 200,
    scan_min_length: 6,
    scan_strip_whitespace: true,
    scan_alphanumeric_only: true,
    scan_token_length: null,
    scan_rate_limit_per_minute: 30,
    scan_dedup_window_seconds: 2
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [appRes, affRes, stationRes] = await Promise.all([
        axios.get(`${API}/admin/app-settings`, { withCredentials: true }),
        axios.get(`${API}/admin/affiliate-settings`, { withCredentials: true }),
        axios.get(`${API}/admin/station-settings`, { withCredentials: true })
      ]);
      setAppSettings(appRes.data);
      setAffiliateSettings(affRes.data);
      setStationSettings(stationRes.data);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAppSettings = async () => {
    try {
      const res = await axios.put(`${API}/admin/app-settings`, appSettings, { withCredentials: true });
      if (res.data.ok === true || res.data.success === true) {
        toast.success("App settings saved");
      } else {
        toast.error(res.data.message || "Failed to save settings");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save settings");
    }
  };

  const handleSaveAffiliateSettings = async () => {
    try {
      await axios.put(`${API}/admin/affiliate-settings`, affiliateSettings, { withCredentials: true });
      toast.success("Affiliate settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    }
  };

  const handleSaveStationSettings = async () => {
    try {
      // Build query params for the PUT endpoint
      const params = new URLSearchParams();
      Object.entries(stationSettings).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== 'pin_configured' && key !== 'station_tokens') {
          params.append(key, value);
        }
      });
      
      await axios.put(`${API}/admin/station-settings?${params.toString()}`, {}, { headers: getAuthHeader() });
      toast.success("Station settings saved");
    } catch (error) {
      toast.error("Failed to save station settings");
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="h-16 border-b border-border px-6 flex items-center bg-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 border border-primary/30 rounded-sm flex items-center justify-center">
            <Settings className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-2xl tracking-tight">SETTINGS</h1>
            <p className="text-xs text-muted-foreground">Application configuration</p>
          </div>
        </div>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-6 max-w-3xl mx-auto space-y-6">
          {/* Station Settings */}
          <Card className="industrial-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ScanBarcode className="w-5 h-5 text-primary" />
                STATION CONFIGURATION
              </CardTitle>
              <CardDescription>
                Configure station ID for tracking scans and operations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground uppercase">Station ID</label>
                <Input
                  value={appSettings.station_id || ''}
                  onChange={(e) => setAppSettings({ ...appSettings, station_id: e.target.value })}
                  placeholder="STATION-1"
                  className="industrial-input font-mono"
                  data-testid="station-id-input"
                />
                <p className="text-xs text-muted-foreground">
                  Unique identifier for this scanning station. Used for event tracking and reports.
                </p>
              </div>
              
              <Button 
                onClick={handleSaveAppSettings} 
                variant="secondary" 
                className="uppercase tracking-wider rounded-sm"
                data-testid="save-station-settings"
              >
                Save Station Settings
              </Button>
            </CardContent>
          </Card>
          
          {/* Receiving Settings */}
          <Card className="industrial-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                RECEIVING VALIDATION
              </CardTitle>
              <CardDescription>
                Control how quantity mismatches are handled during receiving
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground uppercase">Validation Mode</label>
                <Select 
                  value={appSettings.receiving_validation_mode} 
                  onValueChange={(v) => setAppSettings({ ...appSettings, receiving_validation_mode: v })}
                >
                  <SelectTrigger className="industrial-input" data-testid="validation-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strict">
                      <div className="flex flex-col">
                        <span className="font-medium">Strict</span>
                        <span className="text-xs text-muted-foreground">Block mismatched quantities</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="override">
                      <div className="flex flex-col">
                        <span className="font-medium">Override</span>
                        <span className="text-xs text-muted-foreground">Allow with note + audit</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button onClick={handleSaveAppSettings} variant="secondary" className="uppercase tracking-wider rounded-sm" data-testid="save-app-settings">
                Save Validation Settings
              </Button>
            </CardContent>
          </Card>
          
          {/* Card Template Settings */}
          <Card className="industrial-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                KANBAN CARD TEMPLATE
              </CardTitle>
              <CardDescription>
                Default card size for PDF printing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground uppercase">Card Size</label>
                <Select 
                  value={appSettings.card_template_size} 
                  onValueChange={(v) => setAppSettings({ ...appSettings, card_template_size: v })}
                >
                  <SelectTrigger className="industrial-input" data-testid="card-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3x5">3x5 inch (Index Card)</SelectItem>
                    <SelectItem value="4x6">4x6 inch</SelectItem>
                    <SelectItem value="A6">A6 (105mm × 148mm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button onClick={handleSaveAppSettings} variant="secondary" className="uppercase tracking-wider rounded-sm" data-testid="save-card-settings">
                Save Card Settings
              </Button>
            </CardContent>
          </Card>
          
          {/* Affiliate Settings */}
          <Card className="industrial-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary" />
                AMAZON AFFILIATE
              </CardTitle>
              <CardDescription>
                Configure affiliate tag for Amazon supplier links
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground uppercase">Affiliate Tag</label>
                <Input
                  value={affiliateSettings.amazon_affiliate_tag || ""}
                  onChange={(e) => setAffiliateSettings({ ...affiliateSettings, amazon_affiliate_tag: e.target.value })}
                  placeholder="your-tag-20"
                  className="industrial-input font-mono"
                  data-testid="affiliate-tag-input"
                />
                <p className="text-xs text-muted-foreground">
                  Amazon URLs will automatically include this tag when viewing supplier links
                </p>
              </div>
              
              <Button onClick={handleSaveAffiliateSettings} variant="secondary" className="uppercase tracking-wider rounded-sm" data-testid="save-affiliate-settings">
                Save Affiliate Settings
              </Button>
            </CardContent>
          </Card>
          
          {/* Station Mode / Autoscan Settings */}
          <Card className="industrial-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ScanBarcode className="w-5 h-5 text-primary" />
                SCAN STATION SETTINGS
              </CardTitle>
              <CardDescription>
                Configure autoscan behavior for NFC/QR kiosk mode
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Autoscan */}
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-sm">
                <div>
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Zap className="w-4 h-4 text-green-400" />
                    Auto-Submit on Idle
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Automatically submit scan when typing stops (no Enter needed)
                  </p>
                </div>
                <Switch
                  checked={stationSettings.scan_autosubmit_enabled}
                  onCheckedChange={(v) => setStationSettings({ ...stationSettings, scan_autosubmit_enabled: v })}
                  data-testid="autoscan-toggle"
                />
              </div>
              
              {/* Timing Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground uppercase">Idle Submit (ms)</label>
                  <Input
                    type="number"
                    value={stationSettings.scan_idle_submit_ms || 200}
                    onChange={(e) => setStationSettings({ ...stationSettings, scan_idle_submit_ms: parseInt(e.target.value) || 200 })}
                    min={50}
                    max={1000}
                    className="industrial-input font-mono"
                    data-testid="idle-submit-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Submit after input idle for N milliseconds (50-1000)
                  </p>
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground uppercase">Cooldown (ms)</label>
                  <Input
                    type="number"
                    value={stationSettings.scan_submit_cooldown_ms || 200}
                    onChange={(e) => setStationSettings({ ...stationSettings, scan_submit_cooldown_ms: parseInt(e.target.value) || 200 })}
                    min={50}
                    max={1000}
                    className="industrial-input font-mono"
                    data-testid="cooldown-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Delay after submit before allowing next scan (50-1000)
                  </p>
                </div>
              </div>
              
              {/* Length Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground uppercase">Min Token Length</label>
                  <Input
                    type="number"
                    value={stationSettings.scan_min_length || 6}
                    onChange={(e) => setStationSettings({ ...stationSettings, scan_min_length: parseInt(e.target.value) || 6 })}
                    min={1}
                    max={50}
                    className="industrial-input font-mono"
                    data-testid="min-length-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Ignore scans shorter than this (prevents accidental triggers)
                  </p>
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground uppercase">Fixed Length (optional)</label>
                  <Input
                    type="number"
                    value={stationSettings.scan_token_length || ""}
                    onChange={(e) => setStationSettings({ ...stationSettings, scan_token_length: parseInt(e.target.value) || null })}
                    min={0}
                    max={100}
                    placeholder="Disabled"
                    className="industrial-input font-mono"
                    data-testid="fixed-length-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Immediate submit at exact length (0 or empty = disabled)
                  </p>
                </div>
              </div>
              
              {/* Normalization Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-sm">
                  <Switch
                    checked={stationSettings.scan_strip_whitespace}
                    onCheckedChange={(v) => setStationSettings({ ...stationSettings, scan_strip_whitespace: v })}
                    data-testid="strip-whitespace-toggle"
                  />
                  <div>
                    <Label className="text-sm">Strip Whitespace</Label>
                    <p className="text-xs text-muted-foreground">Remove spaces, newlines, tabs</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-sm">
                  <Switch
                    checked={stationSettings.scan_alphanumeric_only}
                    onCheckedChange={(v) => setStationSettings({ ...stationSettings, scan_alphanumeric_only: v })}
                    data-testid="alphanumeric-toggle"
                  />
                  <div>
                    <Label className="text-sm">Alphanumeric Only</Label>
                    <p className="text-xs text-muted-foreground">Keep only A-Z, a-z, 0-9</p>
                  </div>
                </div>
              </div>
              
              {/* Rate Limiting */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground uppercase">Rate Limit (scans/min)</label>
                  <Input
                    type="number"
                    value={stationSettings.scan_rate_limit_per_minute || 30}
                    onChange={(e) => setStationSettings({ ...stationSettings, scan_rate_limit_per_minute: parseInt(e.target.value) || 30 })}
                    min={10}
                    max={100}
                    className="industrial-input font-mono"
                    data-testid="rate-limit-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground uppercase">Dedup Window (sec)</label>
                  <Input
                    type="number"
                    value={stationSettings.scan_dedup_window_seconds || 2}
                    onChange={(e) => setStationSettings({ ...stationSettings, scan_dedup_window_seconds: parseInt(e.target.value) || 2 })}
                    min={1}
                    max={10}
                    className="industrial-input font-mono"
                    data-testid="dedup-window-input"
                  />
                </div>
              </div>
              
              <Button onClick={handleSaveStationSettings} variant="secondary" className="uppercase tracking-wider rounded-sm" data-testid="save-station-settings">
                Save Station Settings
              </Button>
            </CardContent>
          </Card>
        </div>
      </ScrollArea>
    </div>
  );
}
